import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  LocationOn,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from "../../auth/AuthProvider";
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import EditOrganization from '../../components/Organization/EditOrganization';
import { getWarehouseById, getAllPincodes, getPincodeCount, getAdminInfo, getWarehouseCapacity } from '../../services/Organization';
import { IWarehouseDTO, IPincodeResponseDTO, IPincodeCountResponseDTO, IAdminInfoDTO, IWarehouseCapacityDTO } from '../../interfaces/Organization';

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  progress?: number;
  subtext?: string;
}

function StatCard({ title, value, icon, iconBg, iconColor, progress, subtext }: StatCardProps) {
  return (
    <Paper sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: "background.default", height: '140px' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="grey.600" fontWeight={500}>{title}</Typography>
        <Typography variant="h4" fontWeight={700} color="text.primary">{value}</Typography>
        {progress !== undefined && (
          <Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 8, borderRadius: 5, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }}
            />
            {subtext && <Typography variant="caption" color="grey.600" sx={{ mt: 0.5 }}>{subtext}</Typography>}
          </Box>
        )}
      </Box>
      <Box sx={{ bgcolor: iconBg, borderRadius: 1, p: 0.75, color: iconColor,height: 35 }}>
        {icon}
      </Box>
    </Paper>
  );
}

const Organization = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [warehouseData, setWarehouseData] = useState<IWarehouseDTO | null>(null);
  const [pincodes, setPincodes] = useState<IPincodeResponseDTO[]>([]);
  const [pincodeCount, setPincodeCount] = useState<IPincodeCountResponseDTO | null>(null);
  const [adminInfo, setAdminInfo] = useState<IAdminInfoDTO | null>(null);
  const [capacity, setCapacity] = useState<IWarehouseCapacityDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!WAREHOUSE_ID) {
          console.error('Warehouse ID is not available');
          return;
        }
        
        const warehouse = await getWarehouseById(WAREHOUSE_ID);
        setWarehouseData(warehouse);
        
        const allPincodes = await getAllPincodes();
        setPincodes(allPincodes || []);
        
        const countData = await getPincodeCount();
        setPincodeCount(countData);

        const admin = await getAdminInfo(WAREHOUSE_ID);
        setAdminInfo(admin);

        const capacityData = await getWarehouseCapacity(WAREHOUSE_ID);
        setCapacity(capacityData);
      } catch (error) {
        console.error('Error fetching organization data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [WAREHOUSE_ID]);

  if (isEditing) {
    return (
      <EditOrganization
        warehouseData={warehouseData}
        onCancel={() => setIsEditing(false)}
        onSave={() => {
          setIsEditing(false);
          // Refresh data after save
          const fetchData = async () => {
            try {
              if (!WAREHOUSE_ID) {
                console.error('Warehouse ID is not available');
                return;
              }
              const warehouse = await getWarehouseById(WAREHOUSE_ID);
              setWarehouseData(warehouse);
            } catch (error) {
              console.error('Error refreshing data:', error);
            }
          };
          fetchData();
        }}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ 
        height: 'calc(100vh - 3.5rem)', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 3.5rem)', p: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ 
        p: 1, 
        borderRadius: 2, 
        flex: 1, 
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        <Paper sx={{ p: 1, mb:2, bgcolor: 'background.default', display: 'flex', alignItems: 'center', gap: 2, height: '150px' }}>
          <Box sx={{ 
            width: 70, 
            height: 65,
            border: 1,
            borderColor: 'grey.100',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Box sx={{ 
              width: 66, 
              height: 60, 
              borderRadius: '50%', 
              overflow: 'hidden',
              backgroundColor: 'white'
            }}>
              <Box component="img" src={warehouseData?.logo || "/sidebar_Logo.png"} alt="Logo" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={700} sx={{ mb: 0.5,fontSize: 18, }}>{warehouseData?.name || 'Loading...'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <LocationOn sx={{ color: 'grey.600', fontSize: 16, mt: 0.25 }} />
              <Typography variant="caption" color="grey.600">{warehouseData?.address || 'Loading address...'}</Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit sx={{ fontSize: "1.125rem !important", width: "1.125rem", height: "1.75rem" }} />}
            onClick={() => setIsEditing(true)}
            sx={{
              borderRadius: "0.5rem",
              bgcolor: "primary.main",
              boxShadow: "0 0.125rem 0.25rem 0 rgba(0,0,0,0)",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 500,
              lineHeight: "1.25rem",
              textTransform: "none",
              "&:hover": { bgcolor: "primary.main" }
            }}
          >
            Edit Organisation
          </Button>
        </Paper>

        <Grid container spacing={2} sx={{ mb:2 }}>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Available Capacity"
              value={`${(() => {
                const occupiedValue = Math.floor((Number(capacity?.occupiedPercentage) || 0) * 100) / 100;
                const availableValue = Math.floor((100 - occupiedValue) * 100) / 100;
                return availableValue % 1 === 0 ? `${availableValue}%` : `${availableValue.toFixed(2)}%`;
              })()}`}
              icon={<WarehouseIcon />}
              iconBg="rgba(0,94,255,0.1)"
              iconColor="#005eff"
              progress={(() => {
                const occupiedValue = Math.floor((Number(capacity?.occupiedPercentage) || 0) * 100) / 100;
                return 100 - occupiedValue;
              })()}
              subtext={`${(() => {
                const occupiedValue = Math.floor((Number(capacity?.occupiedPercentage) || 0) * 100) / 100;
                return occupiedValue % 1 === 0 ? `${occupiedValue}%` : `${occupiedValue.toFixed(2)}%`;
              })()} utilised`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Delivery Pins"
              value={pincodeCount?.count?.toString() || '0'}
              icon={<DeliveryDiningOutlinedIcon />}
              iconBg="warning.light"
              iconColor="warning.main"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Info sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={700}>General Info</Typography>
            </Box>
            <Paper sx={{ p:1, bgcolor: 'background.default' }}>
              <Grid container spacing={2} sx={{ mb:1}}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="grey.600" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Company Name</Typography>
                  <Typography variant="body2" fontWeight={500}>{adminInfo?.companyName || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="grey.600" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Admin</Typography>
                  <Typography variant="body2" fontWeight={500}>{adminInfo?.adminName || '-'}</Typography>
                </Grid>
              </Grid>
              <Box sx={{ mb:1}}>
                <Typography variant="caption" color="grey.600" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Admin Joined date</Typography>
                <Typography variant="body2" fontWeight={500}>{adminInfo?.joiningDate ? new Date(adminInfo.joiningDate).toLocaleDateString() : '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="grey.600" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Admin Email Address</Typography>
                <Typography variant="body2" fontWeight={500}>{adminInfo?.adminEmail || '-'}</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DeliveryDiningOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={700}>Delivery Pins</Typography>
            </Box>
            <Paper sx={{ bgcolor: 'background.default', p: 1 }}>
              <Box sx={{ 
                height: 150,
                overflow: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none'
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {pincodes.map((pincode) => (
                    <Chip
                      key={pincode.id}
                      label={pincode.pincode}
                      icon={<CheckCircle sx={{ fontSize: 12 }} />}
                      size="small"
                      sx={{ 
                        bgcolor: 'success.light', 
                        color: 'success.main', 
                        border: '1px solid',
                        borderColor: 'grey.100',
                        '& .MuiChip-icon': { color: 'success.main' } 
                      }}
                    />
                  ))}
                  {pincodes.length === 0 && (
                    <Typography variant="body2" color="grey.500" sx={{ p: 1 }}>
                      No delivery pins configured
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ImageOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={700}>Warehouse Images</Typography>
            </Box>
            <Paper sx={{ bgcolor: 'background.default', p: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, height: 150 }}>
                <Box sx={{ position: 'relative', flex: 1, borderRadius: 1, overflow: 'hidden' }}>
                  <Box component="img" src={warehouseData?.insideImgUrl || "/sidebar_Logo.png"} alt="Inside" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left:0,
                      bgcolor: '#000',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    Inside
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', flex: 1, borderRadius: 1, overflow: 'hidden' }}>
                  <Box component="img" src={warehouseData?.outsideImgUrl || "/outbound.svg"} alt="Outside" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      bgcolor: '#000',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    Outside
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
export default Organization;